import { useRef, useState } from 'react';
import { ListSubheader, Menu, MenuItem, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import {
  FACET_LABELS,
  FacetKey,
  Facets,
  isChosen,
  MENU_FACETS,
  TYPE_CHIPS,
  TypeChip,
} from './listFilters';

/**
 * The row of chips under the tabs: one per type with its count, then the
 * filters added with "+ Filter", then "+ Filter" itself.
 *
 * A type is a choice of one, so its chips are toggle buttons of which exactly
 * one is pressed. A filter is a narrowing the reader added, so it is a chip
 * with its own way out — including the ones that arrived with a link, which a
 * reader would otherwise have no way to see or to undo.
 *
 * MUI v4 only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    height: 30,
    padding: theme.spacing(0, 1.25),
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text)',
    font: 'inherit',
    fontSize: '0.8125rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: 'var(--ui-primary)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
    '&[aria-pressed="true"]': {
      borderColor: 'var(--ui-primary)',
      background: 'var(--ui-nav-active-bg)',
      color: 'var(--ui-primary)',
      fontWeight: 600,
    },
  },
  count: {
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--ui-text-muted)',
    fontWeight: 400,
  },
  facet: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: 30,
    maxWidth: '100%',
    padding: theme.spacing(0, 0.5, 0, 1.25),
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--ui-primary)',
    color: 'var(--ui-text)',
    fontSize: '0.8125rem',
  },
  facetText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  facetLabel: { color: 'var(--ui-text-muted)' },
  remove: {
    display: 'inline-grid',
    placeItems: 'center',
    width: 22,
    height: 22,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    color: 'var(--ui-text-muted)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--ui-nav-hover-bg)',
      color: 'var(--ui-text)',
    },
    '& svg': { fontSize: 16 },
  },
  add: {
    borderStyle: 'dashed',
    color: 'var(--ui-text-muted)',
    background: 'transparent',
    '& svg': { fontSize: 16 },
  },
  // A heading, not an entry: smaller, spaced and muted, so the three groups
  // read as three rather than as one list of eleven.
  menuHeading: {
    lineHeight: '28px',
    marginTop: theme.spacing(0.5),
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text-muted)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    fontSize: '0.875rem',
  },
  menuEmpty: { fontSize: '0.875rem', color: 'var(--ui-text-muted)' },
}));

type Offered = {
  key: FacetKey;
  values: { value: string; count: number }[];
}[];

export const FilterChips = ({
  chip,
  chipCounts,
  facets,
  options,
  onChip,
  onAddFacet,
  onRemoveFacet,
}: {
  chip: TypeChip;
  /** Undefined while the list has not arrived: a count not yet known is not 0. */
  chipCounts?: Record<TypeChip, number>;
  facets: Facets;
  /** What "+ Filter" offers, per facet, each value with what it would leave. */
  options: (key: FacetKey) => { value: string; count: number }[];
  onChip: (chip: TypeChip) => void;
  onAddFacet: (key: FacetKey, value: string) => void;
  onRemoveFacet: (key: FacetKey, value: string) => void;
}) => {
  const classes = useStyles();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // What the menu offers, taken when it opens rather than on every render: a
  // closed menu has nothing to count, and the list re-renders on every key
  // typed into the filter. Kept after closing, so the menu does not empty out
  // while it fades.
  const [offered, setOffered] = useState<Offered>([]);
  const openMenu = (anchor: HTMLElement) => {
    setOffered(
      MENU_FACETS.map(key => ({
        key,
        values: options(key).filter(
          ({ value }) => !isChosen(facets, key, value),
        ),
      })),
    );
    setMenuAnchor(anchor);
  };
  const first = offered.find(({ values }) => values.length);
  const firstItem = useRef<HTMLLIElement>(null);
  const chosen = (Object.keys(facets) as FacetKey[]).flatMap(key =>
    (facets[key] ?? []).map(value => ({ key, value })),
  );

  return (
    <div className={classes.row}>
      <div className={classes.row} role="group" aria-label="Type">
        {TYPE_CHIPS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={classes.chip}
            aria-pressed={chip === id}
            onClick={() => onChip(id)}
          >
            {label}{' '}
            {chipCounts && (
              <span className={classes.count}>{chipCounts[id]}</span>
            )}
          </button>
        ))}
      </div>

      {chosen.map(({ key, value }) => (
        <span key={`${key}:${value}`} className={classes.facet}>
          <span className={classes.facetText}>
            <span className={classes.facetLabel}>{FACET_LABELS[key]}:</span>{' '}
            {value}
          </span>
          <button
            type="button"
            className={classes.remove}
            aria-label={`Remove the filter ${FACET_LABELS[key]}: ${value}`}
            onClick={() => onRemoveFacet(key, value)}
          >
            <CloseIcon />
          </button>
        </span>
      ))}

      <button
        type="button"
        className={`${classes.chip} ${classes.add}`}
        aria-haspopup="menu"
        aria-expanded={menuAnchor ? 'true' : undefined}
        onClick={e => openMenu(e.currentTarget)}
      >
        <AddIcon /> Filter
      </button>
      {/*
        The focus goes to the first value, not to the menu's first child: that
        is a heading, and MUI would focus it. So MUI focuses the menu itself,
        and the first value takes the focus as the menu opens. Headings are
        presentation, so the menu holds only its values — each named with its
        facet, which is what the heading says to a reader who sees it.
      */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        variant="menu"
        disableAutoFocusItem
        TransitionProps={{ onEntering: () => firstItem.current?.focus() }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ style: { maxHeight: 420, minWidth: 240 } }}
      >
        {/* A flat list of items: MUI's menu reads its children one by one
            for its keyboard focus, and a fragment would be one item. */}
        {offered.flatMap(({ key, values }) => [
          <ListSubheader
            key={key}
            role="presentation"
            className={classes.menuHeading}
          >
            {FACET_LABELS[key]}
          </ListSubheader>,
          ...(values.length === 0
            ? [
                <MenuItem
                  key={`${key}:none`}
                  disabled
                  aria-label={`${FACET_LABELS[key]}: nothing more to narrow by`}
                  className={classes.menuEmpty}
                >
                  Nothing more to narrow by
                </MenuItem>,
              ]
            : values.map(({ value, count }) => (
                <MenuItem
                  key={`${key}:${value}`}
                  ref={
                    key === first?.key && value === first.values[0].value
                      ? firstItem
                      : undefined
                  }
                  aria-label={`${FACET_LABELS[key]}: ${value}, ${count}`}
                  className={classes.menuItem}
                  onClick={() => {
                    setMenuAnchor(null);
                    onAddFacet(key, value);
                  }}
                >
                  <span>{value}</span>
                  <span className={classes.count}>{count}</span>
                </MenuItem>
              ))),
        ])}
      </Menu>
    </div>
  );
};
